import root from 'window-or-global';
import React from 'react';
import SocialChannel from './SocialChannel';
import influencerActions from '../../../actions/influencer.actions';
import trackingActions, {EVENT_ACTION, EVENT_CATEGORY} from '../../../actions/tracking.actions';
import {connect} from 'react-redux';
import {getMessage} from 'epic-diesel-common';
import {
    canRemoveChannel,
    isCreativeBuilder,
    isNewApplicant,
    isValidChannel
} from '../../../utils/ValidationUtils';
import oauthActions from '../../../actions/oauth.actions';
import errorActions from '../../../actions/error.actions';
import {canAutoSelectMainChannel, isValidSocialChannels} from '../../../utils/ValidationUtils';
import {
    Affiliate,
    ChannelStatus,
    ErrorCode,
    SocialChannel as ISocialChannel,
    SocialPlatform
} from '../../../../shared/types';
import {mapPlatformToUrl} from '../../../../shared/utils/SocialUtils';
import {clientAuthError} from '../../../utils/SocialUtils';

//@ts-ignore
import styles from './SocialChannelSection.pcss';

const platformTypes = Object.keys(SocialPlatform);

const mapDispatchToProps = dispatch => ({
    sendBeginAuthChannelEvent: platform => {
        dispatch(
            trackingActions.trackInteractionEvent(
                EVENT_CATEGORY.SocialChannel,
                EVENT_ACTION.BeginAuthChannel,
                {platform}
            )
        );
    },
    sendAuthChannelSuccessEvent: platform => {
        dispatch(
            trackingActions.trackInteractionEvent(
                EVENT_CATEGORY.SocialChannel,
                EVENT_ACTION.AuthChannelSuccess,
                {platform}
            )
        );
    },
    sendAuthChannelFailEvent: platform => {
        dispatch(
            trackingActions.trackInteractionEvent(
                EVENT_CATEGORY.SocialChannel,
                EVENT_ACTION.AuthChannelFail,
                {platform}
            )
        );
    },
    resetDisconnectedPlatform: platform => {
        dispatch(influencerActions.resetDisconnectedPlatform({platform}));
    },
    getOAuthLink: platform => {
        dispatch(oauthActions.getOAuthLink(platform));
    },
    resetOAuth: platform => {
        dispatch(oauthActions.resetOAuth({platform}));
    },
    showError: errorCode => {
        dispatch(errorActions.showErrorMessage({errorCode}));
    }
});

export interface Props {
    messages: any;
    configState: any;
    affiliate: Affiliate;
    getSocialChannels: VoidFunction;
    mainChannelPlatform: string;
    channels: ISocialChannel[];
    updateForm: (data) => void;
    updateFormErrors: (hasError?: boolean) => void;
    getOAuthLink: (platform: string) => void;
    resetOAuth: (platform: string) => void;
    disconnectSocialChannel: (channel: ISocialChannel, severConnection?: boolean) => void;
    resetDisconnectedPlatform: (platform: string) => void;
    sendBeginAuthChannelEvent: (platform: string) => void;
    sendAuthChannelSuccessEvent: (platform: string) => void;
    sendAuthChannelFailEvent: (platform: string) => void;
    showError: (errorCode: string) => void;
}

interface State {
    showError: boolean;
    channelValidated: boolean;
    allChannelsIsValid: boolean;
}

class SocialChannelSection extends React.PureComponent<Props, State> {
    state = {
        showError: false,
        channelValidated: true,
        allChannelsIsValid: true
    };

    componentDidMount() {
        if (root.document) {
            root.document.addEventListener('setAuthResponse', this.onSetAuthResponse);
            root.document.addEventListener('closeAuthPopup', this.onCloseAuthPopup);
            root.addEventListener('storage', this.onStorageAuthResponse);
        }
    }

    componentWillUnmount() {
        if (root.document) {
            root.document.removeEventListener('setAuthResponse', this.onSetAuthResponse);
            root.document.removeEventListener('closeAuthPopup', this.onCloseAuthPopup);
            root.removeEventListener('storage', this.onStorageAuthResponse);
        }
    }

    onRemoveChannel = (index, severConnection) => {
        const channels = [...this.props.channels];
        const removeChannel = channels[index];
        const {id: isAdded, oauthDetails, mainChannel} = removeChannel || {};
        const {valid} = oauthDetails || {};

        if (severConnection || isAdded || valid === true) {
            this.props.disconnectSocialChannel(removeChannel, severConnection);
        } else {
            // remove ch
            channels.splice(index, 1);

            // reset main channel
            let mainChannelPlatform = this.props.mainChannelPlatform;

            if (!channels.length || mainChannel) {
                mainChannelPlatform = null;
            }

            // auto-select new main channel
            if (!mainChannelPlatform) {
                const foundChannelIndex = channels.findIndex(isValidChannel);
                if (channels[foundChannelIndex]) {
                    channels[foundChannelIndex].mainChannel = true;
                    mainChannelPlatform = channels[foundChannelIndex].platform;
                }
            }

            // Update parent form
            this.props.updateForm({
                channels,
                mainChannelPlatform
            });

            if (this.state.showError) {
                this.updateFormErrors();
            }
        }
    };

    onAddChannel = (channelData = null) => {
        if (!channelData) return;

        const channels = [...this.props.channels];
        const {platform} = channelData || {};

        const foundChannelIndex = channels.findIndex(ch => ch.platform === platform);

        // if exists update, else push new
        if (foundChannelIndex !== -1) {
            Object.assign(channels[foundChannelIndex], {...channelData, id: null});
        } else if (channelData && platform) {
            channels.push(channelData);
        }

        // Update parent form
        this.props.updateForm({channels});

        if (this.state.showError) {
            this.updateFormErrors();
        }
    };

    updateFormErrors = () => {
        const {channels} = this.props;
        let hasError = false;
        let channelValidated = true;
        let allChannelsIsValid = true;

        if (channels.length === 0) {
            channelValidated = false;
            hasError = true;
        } else if (!isValidSocialChannels(channels)) {
            allChannelsIsValid = false;
            hasError = true;
        }

        this.setState({
            channelValidated,
            allChannelsIsValid,
            showError: hasError
        });

        // update parent form
        return this.props.updateFormErrors(hasError);
    };

    updateChannelData = (channelData: Partial<ISocialChannel>) => {
        if (!this.props.channels.length) return;

        const channels = [...this.props.channels];
        const foundChannelIndex = channels.findIndex(ch => ch.platform === channelData.platform);

        if (foundChannelIndex === -1) {
            console.error('Unable to find channel');
            return;
        }

        // Overwrite channel with new data
        for (const key in channelData) {
            if (channelData[key]) {
                channels[foundChannelIndex][key] = channelData[key];
            }
        }

        const data = {
            channels
        };

        // Auto select main channel for first VALID entry
        if (isValidChannel(channels[foundChannelIndex]) && canAutoSelectMainChannel(channels)) {
            channels[foundChannelIndex].mainChannel = true;
            Object.assign(data, {
                mainChannelPlatform: channels[foundChannelIndex].platform
            });
        }

        // Update parent form
        this.props.updateForm(data);

        if (this.state.showError) {
            this.updateFormErrors();
        }
    };

    onSetAuthResponse = event => {
        if (!event.detail) return;

        const {
            channelStatus,
            platform,
            // authState,
            errors,
            // impossibleCheck,
            followers,
            valid,
            channelId
        } = event.detail;

        if (channelStatus === ChannelStatus.VALID) {
            this.props.sendAuthChannelSuccessEvent(platform);
            this.props.resetDisconnectedPlatform(platform);
            this.props.getSocialChannels();
        } else {
            this.props.sendAuthChannelFailEvent(platform);
        }

        const channelData: Partial<ISocialChannel> = {
            platform,
            channelId,
            link: null,
            followers,
            channelStatus,
            violations: [],
            oauthDetails: {
                valid: valid === 'true' || valid
            }
        };

        if (typeof errors === 'string') {
            channelData.violations = errors.split(',');
        } else if (Array.isArray(errors)) {
            channelData.violations = errors;
        }

        if (channelId) {
            channelData.link = mapPlatformToUrl(platform, channelId);
        }

        this.updateChannelData(channelData);
    };

    onStorageAuthResponse = event => {
        try {
            if (!(event.key in SocialPlatform) || event.oldValue || !event.newValue) {
                return;
            }
            const detail = JSON.parse(event.newValue);
            if (detail) {
                this.onSetAuthResponse({detail});
            }
        } catch (e) {
            //emit the error
            console.warn(e);
        }
    };

    onCloseAuthPopup = (event: CustomEventInit) => {
        if (!event.detail || !event.detail.platform) return;
        const {platform} = event.detail;
        let channel;

        // reset auth state
        this.props.resetOAuth(platform);

        if ((channel = this.props.channels.find(ch => ch.platform === platform))) {
            const {oauthDetails, impossibleCheck, violations = []} = channel || {};
            const {valid} = oauthDetails || {};
            if (!impossibleCheck && !valid) {
                const authResponse = clientAuthError();

                const errors = violations.length ? violations : authResponse.violations;

                this.onSetAuthResponse({
                    detail: {
                        ...authResponse,
                        errors,
                        platform
                    }
                });
            }
        }
    };

    render() {
        const {configState, messages, channels, affiliate} = this.props;
        const {socialChannelsProperties = {}, disableSocials = {}, quickLinks = {}} =
            configState || {};

        const newApplicant = isNewApplicant(affiliate);
        const isCreative = isCreativeBuilder(affiliate);

        return (
            <>
                {!isCreative && (
                    <p>
                        <small>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: getMessage(
                                        messages,
                                        'epic.affiliate.social.channels.acknowledge.v2',
                                        [
                                            socialChannelsProperties.MINIMUM_FOLLOWERS,
                                            quickLinks.howToLinkSocials
                                        ]
                                    )
                                }}
                            />
                        </small>
                    </p>
                )}

                <div className={styles.channelWrap}>
                    {platformTypes.map((platform, index) => {
                        const foundIndex = channels.findIndex(ch => ch.platform === platform);
                        const channel = channels[foundIndex];

                        return (
                            <SocialChannel
                                key={platform}
                                channel={channel}
                                platform={platform as SocialPlatform}
                                isNewApplicant={newApplicant}
                                enablePlatform={!disableSocials[platform.toLowerCase()]}
                                messages={messages}
                                configState={configState}
                                affiliate={affiliate}
                                getSocialChannels={this.props.getSocialChannels}
                                onAddChannel={this.onAddChannel}
                                updateChannelData={this.updateChannelData}
                                getOAuthLink={this.props.getOAuthLink}
                                sendBeginAuthChannelEvent={this.props.sendBeginAuthChannelEvent}
                                onDisconnect={(e, severConnection) => {
                                    if (!channel) return;

                                    if (
                                        severConnection ||
                                        canRemoveChannel(affiliate, channel, false)
                                    ) {
                                        this.onRemoveChannel(foundIndex, severConnection);
                                    } else {
                                        this.props.showError(
                                            ErrorCode.SOCIAL_CHANNEL_PRIMARY_LOCKED
                                        );
                                    }
                                }}
                            />
                        );
                    })}
                </div>
            </>
        );
    }
}

export default connect(
    null,
    mapDispatchToProps,
    null,
    {forwardRef: true}
)(SocialChannelSection);
